import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { hhmm, todayStr } from "@/lib/format";
import { pendingEvents, queueEvent, syncQueue } from "@/lib/offline";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckSquare, MapPin, NavigationArrow, Path, QrCode, Square, WifiSlash, FileText } from "@phosphor-icons/react";

const getPos = () =>
  new Promise((res) => {
    if (!navigator.geolocation) return res(null);
    navigator.geolocation.getCurrentPosition(
      (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => res(null),
      { timeout: 8000, enableHighAccuracy: true }
    );
  });

export default function MobileHome() {
  const [shifts, setShifts] = useState([]);
  const [queued, setQueued] = useState(0);
  const [busy, setBusy] = useState("");
  const [qrFor, setQrFor] = useState(null);
  const [qrCode, setQrCode] = useState("");
  const [poFor, setPoFor] = useState(null);
  const [clFor, setClFor] = useState(null);
  const [clSaving, setClSaving] = useState("");
  const [tourFor, setTourFor] = useState(null);
  const [tours, setTours] = useState({});
  const [cpCode, setCpCode] = useState("");
  const [scanning, setScanning] = useState("");

  const load = () => {
    api.get("/attendance/my").then((r) => setShifts(r.data)).catch((e) => toast.error(errMsg(e)));
    pendingEvents().then((p) => setQueued(p.length));
  };

  useEffect(() => {
    load();
    const doSync = async () => {
      try {
        const res = await syncQueue(api);
        if (res.synced || res.duplicates) {
          toast.success(`Synced ${res.synced} queued event(s)${res.duplicates ? ` · ${res.duplicates} duplicate(s) skipped` : ""}`);
          load();
        }
      } catch (e) { /* still offline */ }
    };
    doSync();
    window.addEventListener("online", doSync);
    return () => window.removeEventListener("online", doSync);
  }, []);

  const punch = async (shift, event_type, method = "gps") => {
    setBusy(shift.id + event_type);
    const pos = method === "gps" ? await getPos() : null;
    const ev = {
      client_event_id: crypto.randomUUID(),
      shift_id: shift.id,
      event_type,
      device_ts: new Date().toISOString(),
      lat: pos?.lat ?? null,
      lng: pos?.lng ?? null,
      method,
    };
    try {
      const { data } = await api.post("/attendance/event", ev);
      if (data.geo_status === "out_of_geofence") toast.warning(`Recorded, but you are ${data.distance_m}m from the site — supervisor alerted`);
      else toast.success(`${event_type === "check_in" ? "Checked in" : "Checked out"}${data.distance_m != null ? ` · ${data.distance_m}m from post` : ""}`);
    } catch (e) {
      await queueEvent(ev);
      setQueued((q) => q + 1);
      toast.warning("No connection — event saved on this device and will sync automatically");
    }
    setBusy("");
    load();
  };

  const respond = async (shift, accept) => {
    try {
      await api.post(`/shifts/${shift.id}/respond`, { accept });
      toast.success(accept ? "Shift accepted" : "Declined — dispatch notified");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const ack = async (shift) => {
    try {
      await api.post(`/shifts/${shift.id}/ack`);
      toast.success("Assignment acknowledged");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const ackPostOrders = async (shift) => {
    try {
      await api.post(`/sites/${shift.site_id}/ack-post-orders`);
      toast.success("Post orders acknowledged");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitQr = async (shift) => {
    if (qrCode.trim().toUpperCase() !== (shift.site?.qr_code || "").toUpperCase()) {
      toast.error("QR code does not match this site");
      return;
    }
    setQrFor(null);
    setQrCode("");
    punch(shift, "check_in", "qr");
  };

  // Ticking a box saves it. There is no Save button on purpose: a guard walking
  // a perimeter ticks one item, pockets the phone and walks on, and a checklist
  // that needed a second deliberate action would be lost every time.
  const toggleCheck = async (shift, itemId) => {
    const done = new Set((shift.checklist?.items || []).filter((i) => i.done).map((i) => i.item_id));
    done.has(itemId) ? done.delete(itemId) : done.add(itemId);
    setClSaving(itemId);
    try {
      const { data } = await api.post(`/shifts/${shift.id}/checklist`, { done: [...done] });
      setShifts((prev) => prev.map((x) => (x.id === shift.id ? { ...x, checklist: data.checklist } : x)));
    } catch (e) {
      toast.error(errMsg(e));
    }
    setClSaving("");
  };

  const openTour = async (shift) => {
    if (tourFor === shift.id) { setTourFor(null); return; }
    setTourFor(shift.id);
    try {
      const { data } = await api.get(`/shifts/${shift.id}/tour`);
      setTours((p) => ({ ...p, [shift.id]: data }));
    } catch (e) { toast.error(errMsg(e)); }
  };

  // ⚠️ The scan carries the handset's OWN timestamp. A round walked in a
  // basement with no signal syncs later, and without `device_ts` every one of
  // those scans would land in whichever round happened to be open when the
  // phone found a bar of reception.
  const scanCheckpoint = async (shift, code) => {
    setScanning(code);
    const pos = await getPos();
    try {
      const { data } = await api.post(`/shifts/${shift.id}/tour-scan`, {
        code, method: "qr", device_ts: new Date().toISOString(),
        lat: pos?.lat ?? null, lng: pos?.lng ?? null,
      });
      setTours((p) => ({ ...p, [shift.id]: { ...data.tour, shift: p[shift.id]?.shift } }));
      setCpCode("");
      const r = data.tour.rounds.find((x) => x.state !== "complete" && x.due) || {};
      toast.success(`${data.scan.checkpoint_label} logged${r.missed?.length ? ` · ${r.missed.length} left this round` : ""}`);
    } catch (e) { toast.error(errMsg(e)); }
    setScanning("");
  };

  // Directions are a LINK, not a map we render. The guard already has a maps app
  // that knows about traffic and jeepney routes; duplicating it badly inside a
  // shift card would be worse than handing the coordinates over.
  const directionsHref = (s) =>
    s.site?.lat != null && s.site?.lng != null
      ? `https://www.google.com/maps/dir/?api=1&destination=${s.site.lat},${s.site.lng}`
      : null;

  const today = todayStr();

  return (
    <div className="p-4" data-testid="mobile-home">
      {queued > 0 && (
        <div className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10 p-3 mb-4 flex items-center gap-2" data-testid="offline-queue-banner">
          <WifiSlash size={16} />
          <span className="font-mono text-xs">{queued} event(s) queued offline — will auto-sync</span>
        </div>
      )}
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-3">My assignments</div>
      {shifts.length === 0 && <div className="font-mono text-xs text-muted-foreground border border-border p-6" data-testid="no-assignments">No upcoming assignments</div>}

      {/* ⚠️ A guard whose next shift is tomorrow used to open this screen, find
          no CHECK IN button anywhere, and have nothing tell them why. "There is
          no clock in on the app" is a fair reading of a screen that shows five
          assignments and no way to punch any of them. */}
      {shifts.length > 0 && !shifts.some((x) => {
        const st = new Date(`${x.date}T${x.start_time || "00:00"}:00+08:00`);
        const en = new Date(`${x.date}T${x.end_time || "00:00"}:00+08:00`);
        if (en <= st) en.setDate(en.getDate() + 1);
        return Date.now() >= st.getTime() - 4 * 3600e3 && Date.now() <= en.getTime() + 12 * 3600e3;
      }) && (
        <div className="border border-border p-4 mb-3 text-[11px] leading-snug text-muted-foreground"
             data-testid="nothing-to-punch">
          <span className="text-foreground font-bold">Nothing to clock into right now.</span>{" "}
          The CHECK IN button appears on a shift from four hours before it starts, and CHECK OUT
          stays until well after it ends — including the next morning on a night shift. Your next
          one is <span className="font-mono text-foreground">{shifts[0].date} {hhmm(shifts[0].start_time)}</span>.
        </div>
      )}
      <div className="space-y-3">
        {shifts.map((s) => {
          const isToday = s.date === today;
          const hasIn = (s.events || []).some((e) => e.event_type === "check_in");
          const hasOut = (s.events || []).some((e) => e.event_type === "check_out");

          // ⚠️ A NIGHT SHIFT IS DATED THE DAY IT STARTS, so at 07:00 the next
          // morning `s.date === today` is FALSE — and both punch buttons used to
          // hang off exactly that. A guard on 19:00–07:00 could clock in and
          // then had no way to clock out: the button vanished at midnight.
          //
          // It is not cosmetic. `compute_period` prices a shift with no
          // check-out at ZERO hours with "missing check-in/out", so a full night
          // on post is paid nothing, and it is every night post on the roster.
          //
          // The codebase already carries this lesson — `roll_forward` has a
          // paragraph about a night shift ending the next morning. Fixed there,
          // live here.
          const win = (() => {
            const start = new Date(`${s.date}T${s.start_time || "00:00"}:00+08:00`);
            const end = new Date(`${s.date}T${s.end_time || "00:00"}:00+08:00`);
            if (end <= start) end.setDate(end.getDate() + 1);
            return { start, end };
          })();
          const nowMs = Date.now();
          const HOUR = 3600e3;
          // Early arrival is normal on a detachment, so the door opens before
          // the hour rather than on it.
          const canCheckIn = !hasIn && ["published", "accepted"].includes(s.status)
            && nowMs >= win.start.getTime() - 4 * HOUR
            && nowMs <= win.end.getTime() + 4 * HOUR;
          // A guard who forgot to sign off must still be able to. The punch
          // carries its own timestamp and a supervisor can correct it; hiding
          // the exit only produces an unpaid shift.
          const canCheckOut = hasIn && !hasOut && nowMs <= win.end.getTime() + 12 * HOUR;
          const onNow = nowMs >= win.start.getTime() && nowMs <= win.end.getTime();
          // The site's list, merged with whatever this shift has recorded against
          // it. Read from the SITE so an item added after the guard first opened
          // the list still appears, rather than the shift freezing yesterday's.
          const items = s.site?.checklists || [];
          const doneIds = new Set((s.checklist?.items || []).filter((i) => i.done).map((i) => i.item_id));
          const cl = items.length
            ? { total: items.length, done_count: [...doneIds].filter((id) => items.some((i) => i.id === id)).length }
            : null;
          if (cl) cl.complete = cl.done_count === cl.total;
          const dir = directionsHref(s);
          return (
            <div key={s.id} className={`border bg-card p-4 ${onNow || isToday ? "border-[hsl(var(--warning))]" : "border-border"}`} data-testid={`assignment-${s.ref_no}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[10px] text-muted-foreground">{s.ref_no}</span>
                <StatusBadge value={s.status} />
              </div>
              <div className="font-heading font-bold text-lg leading-tight">{s.site_name}</div>
              <div className="text-xs text-muted-foreground">{s.post_name || "Any post"} · {s.client_name}</div>
              <div className="font-mono text-sm mt-2">
                {s.date} · {hhmm(s.start_time)} – {hhmm(s.end_time)}
                {/* Spelled out, because "19:00 – 07:00" on one date reads as a
                    shift that ended twelve hours before it started. */}
                {win.end.getDate() !== win.start.getDate() && (
                  <span className="text-muted-foreground"> (next morning)</span>
                )}
                {" "}<StatusBadge value={s.shift_type} />
              </div>
              {s.site?.geofence_radius_m && (
                <div className="font-mono text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                  <MapPin size={12} /> Geofence {s.site.geofence_radius_m}m
                </div>
              )}

              {s.status === "offered" && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button className="rounded-none" data-testid={`accept-${s.ref_no}`} onClick={() => respond(s, true)}>Accept</Button>
                  <Button variant="outline" className="rounded-none" data-testid={`decline-${s.ref_no}`} onClick={() => respond(s, false)}>Decline</Button>
                </div>
              )}
              {s.status === "published" && !s.acknowledged && (
                <Button className="w-full rounded-none mt-3" variant="outline" data-testid={`ack-${s.ref_no}`} onClick={() => ack(s)}>Acknowledge assignment</Button>
              )}
              {canCheckIn && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button className="rounded-none h-12 font-heading font-bold" data-testid={`checkin-${s.ref_no}`} disabled={busy === s.id + "check_in"} onClick={() => punch(s, "check_in", "gps")}>
                    {busy === s.id + "check_in" ? "LOCATING…" : "CHECK IN (GPS)"}
                  </Button>
                  {/* ⚠️ This used to be `setQrFor(s)` -- the whole shift object --
                      while the panel below rendered on `qrFor === s.id`. An
                      object is never equal to a string, so the panel could not
                      open in any circumstance and QR check-in had never worked
                      once since it was written. It is the ONLY way to punch in
                      where there is no GPS fix, which is the basement car park,
                      the stockroom and the mall service corridor -- exactly the
                      posts where a guard stands. */}
                  <Button variant="outline" className="rounded-none h-12" data-testid={`qr-${s.ref_no}`} onClick={() => setQrFor(qrFor === s.id ? null : s.id)}>
                    <QrCode size={18} className="mr-1" /> QR
                  </Button>
                </div>
              )}
              {canCheckOut && (
                <>
                  {/* Named BEFORE the button, not after it. Post orders item 5
                      makes the checklist part of the shift, so a guard about to
                      end one should see what is outstanding while they can still
                      walk back and do it. It never blocks the punch: holding a
                      guard's check-out hostage to a tick box costs them paid
                      hours for a clerical miss. */}
                  {cl && !cl.complete && (
                    <button
                      type="button"
                      className="w-full text-left border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10 p-2.5 mt-3 flex items-center gap-2"
                      data-testid={`checklist-nudge-${s.ref_no}`}
                      onClick={() => setClFor(s.id)}
                    >
                      <CheckSquare size={15} className="text-[hsl(var(--warning))] shrink-0" />
                      <span className="text-[11px] leading-snug">
                        <span className="font-bold">{cl.total - cl.done_count} checklist item(s) still open.</span>{" "}
                        Post orders ask you to finish these before you sign off. Tap to open.
                      </span>
                    </button>
                  )}
                  <Button className="w-full rounded-none mt-3 h-12 font-heading font-bold" data-testid={`checkout-${s.ref_no}`} disabled={busy === s.id + "check_out"} onClick={() => punch(s, "check_out", "gps")}>
                    {busy === s.id + "check_out" ? "LOCATING…" : "CHECK OUT (GPS)"}
                  </Button>
                </>
              )}
              {hasIn && hasOut && <div className="font-mono text-[10px] text-[hsl(var(--success))] mt-3" data-testid={`done-${s.ref_no}`}>SHIFT COMPLETE — TIME RECORDED</div>}
              <div className="flex flex-wrap gap-1 mt-3 -mx-1">
                <Button size="sm" variant="ghost" className="text-xs h-8" data-testid={`postorders-${s.ref_no}`} onClick={() => setPoFor(poFor === s.id ? null : s.id)}>
                  <FileText size={14} className="mr-1" /> Post orders v{s.site?.post_orders_version}
                </Button>
                {cl && (
                  <Button size="sm" variant="ghost" className="text-xs h-8" data-testid={`checklist-${s.ref_no}`} onClick={() => setClFor(clFor === s.id ? null : s.id)}>
                    <CheckSquare size={14} className="mr-1" /> Checklist {cl.done_count}/{cl.total}
                  </Button>
                )}
                {(s.site?.checkpoints || []).length > 0 && s.site?.patrol_interval_hours > 0 && (
                  <Button size="sm" variant="ghost" className="text-xs h-8" data-testid={`patrol-${s.ref_no}`} onClick={() => openTour(s)}>
                    <Path size={14} className="mr-1" /> Patrol
                    {/*
                      ⚠️ "0/0" is not a score, it is a shift that has not started.
                      A guard glancing at tomorrow's card cannot tell it apart from
                      a round they have failed — the same reason the supervisor's
                      screen reports "nothing due yet" instead of 0%.
                    */}
                    {tours[s.id]
                      ? (tours[s.id].rounds_due > 0
                          ? ` ${tours[s.id].complete_rounds}/${tours[s.id].rounds_due}`
                          : ` · ${tours[s.id].expected_rounds} to come`)
                      : ""}
                  </Button>
                )}
                {dir && (
                  <a href={dir} target="_blank" rel="noreferrer" data-testid={`directions-${s.ref_no}`}
                     className="inline-flex items-center h-8 px-3 text-xs text-muted-foreground hover:text-foreground">
                    <NavigationArrow size={14} className="mr-1" /> Directions
                  </a>
                )}
              </div>
              {s.site?.address && <div className="text-[10px] text-muted-foreground leading-snug">{s.site.address}</div>}
              {clFor === s.id && (
                <div className="mt-2 border border-border p-3" data-testid={`checklist-panel-${s.ref_no}`}>
                  <div className="text-[10px] uppercase tracking-wider font-bold mb-2">Site checklist</div>
                  <div className="space-y-1">
                    {items.map((it) => {
                      const on = doneIds.has(it.id);
                      return (
                        <button
                          key={it.id}
                          type="button"
                          data-testid={`checklist-item-${it.id.slice(0, 8)}`}
                          disabled={clSaving === it.id}
                          onClick={() => toggleCheck(s, it.id)}
                          className="w-full flex items-center gap-2.5 py-2 text-left disabled:opacity-50"
                        >
                          {on
                            ? <CheckSquare size={20} weight="fill" className="text-[hsl(var(--success))] shrink-0" />
                            : <Square size={20} className="text-muted-foreground shrink-0" />}
                          <span className={`text-sm leading-snug ${on ? "line-through text-muted-foreground" : ""}`}>{it.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  {s.checklist?.updated_at && (
                    <div className="font-mono text-[9px] text-muted-foreground mt-2 pt-2 border-t border-border">
                      Last saved {s.checklist.updated_at.slice(0, 16).replace("T", " ")}
                    </div>
                  )}
                </div>
              )}
              {poFor === s.id && (
                <div className="mt-2 border border-border p-3">
                  <pre className="whitespace-pre-wrap font-mono text-[11px] text-muted-foreground" data-testid="post-orders-text">{s.site?.post_orders || "No post orders."}</pre>
                  <Button size="sm" className="rounded-none mt-2 w-full" data-testid={`ack-postorders-${s.ref_no}`} onClick={() => ackPostOrders(s)}>I have read & understood</Button>
                </div>
              )}
              {tourFor === s.id && (
                <div className="mt-2 border border-border p-3" data-testid={`patrol-panel-${s.ref_no}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold">
                      Patrol · every {s.site.patrol_interval_hours}h
                    </span>
                    {tours[s.id] && (
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {tours[s.id].rounds_due > 0
                          ? `${tours[s.id].complete_rounds}/${tours[s.id].rounds_due} round(s) done`
                          : "nothing due yet"}
                        {tours[s.id].pending_rounds ? ` · ${tours[s.id].pending_rounds} to come` : ""}
                      </span>
                    )}
                  </div>

                  {/* Scan by typing the code off the sticker -- the same fallback
                      as the QR punch, and for the same reason: a basement has no
                      signal and often no working camera focus either. */}
                  <div className="flex gap-2 mb-3">
                    <Input className="rounded-none font-mono h-9" data-testid="cp-code-input"
                           value={cpCode} onChange={(e) => setCpCode(e.target.value)}
                           placeholder={s.site.checkpoints[0]?.code || "SIT-0000-CP1"} />
                    <Button className="rounded-none h-9" data-testid="cp-scan"
                            disabled={!cpCode.trim() || !!scanning}
                            onClick={() => scanCheckpoint(s, cpCode.trim())}>
                      Log it
                    </Button>
                  </div>

                  {/* One tap per checkpoint. A guard standing at the fire exit
                      should not have to type a code they can see. */}
                  <div className="space-y-1 mb-3">
                    {s.site.checkpoints.map((cp) => {
                      const cur = (tours[s.id]?.rounds || []).find((r) => r.due && r.state !== "complete")
                        || (tours[s.id]?.rounds || []).filter((r) => r.due).slice(-1)[0];
                      const done = cur && !(cur.missed || []).includes(cp.label);
                      return (
                        <button key={cp.id} type="button"
                                data-testid={`cp-${cp.code}`}
                                disabled={scanning === cp.code}
                                onClick={() => scanCheckpoint(s, cp.code)}
                                className="w-full flex items-center gap-2.5 py-2 text-left disabled:opacity-50">
                          {done
                            ? <CheckSquare size={20} weight="fill" className="text-[hsl(var(--success))] shrink-0" />
                            : <Square size={20} className="text-muted-foreground shrink-0" />}
                          <span className="min-w-0">
                            <span className={`block text-sm leading-snug ${done ? "line-through text-muted-foreground" : ""}`}>{cp.label}</span>
                            <span className="block font-mono text-[9px] text-muted-foreground">{cp.code}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {(tours[s.id]?.rounds || []).map((r) => (
                    <div key={r.round} className="flex items-center justify-between border-t border-border py-1 text-[11px]">
                      <span className="font-mono">{r.from}–{r.to}</span>
                      <span className={
                        r.state === "complete" ? "text-[hsl(var(--success))]"
                          : r.state === "pending" ? "text-muted-foreground"
                            : "text-[hsl(var(--alert))] font-semibold"}>
                        {r.state === "pending" ? "not due yet" : `${r.scanned}/${r.of} ${r.state === "complete" ? "" : r.state.replace("_", " ")}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {qrFor === s.id && (
                <div className="mt-3 border border-[hsl(var(--warning))] p-3" data-testid="qr-entry-panel">
                  <div className="text-[10px] uppercase tracking-wider font-bold mb-1">Enter code shown on site QR</div>
                  <div className="flex gap-2">
                    <Input className="rounded-none font-mono" data-testid="qr-code-input" value={qrCode} onChange={(e) => setQrCode(e.target.value)} placeholder="SIT-2026-0001-QR" />
                    <Button className="rounded-none" data-testid="qr-submit" onClick={() => submitQr(s)}>Verify</Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
